import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuiz } from '../../context/QuizContext';
import { useLanguage } from '../../context/LanguageContext';
import { useHelp } from '../../context/HelpContext';
import ExamResults from '../exams/components/ExamResults';
import ExamHeader from '../exams/components/ExamHeader';
import ExamMiniNav from '../exams/components/ExamMiniNav';
import ExamGridOverlay from '../exams/components/ExamGridOverlay';
import ExamQuestion from '../exams/components/ExamQuestion';

const QuizSession = () => {
    const { language } = useLanguage();
    const {
        onDemandAnswers,
        questions,
        answers,
        currentQuestionIndex,
        setCurrentQuestionIndex,
        mode,
        isPaused,
        setIsPaused,
        isFinished,
        timeInSeconds,
        isLoading,
        submitAnswer,
        loadAnswers,
        finishQuiz,
        restartQuiz,
        stopQuiz,
        courseTitle,
    } = useQuiz();

    const { startTour, hasSeenPage, helpPrefs, isPrefsSynced, registerOverlay, unregisterOverlay } = useHelp();

    // Register this overlay so background page tours are blocked
    useEffect(() => {
        registerOverlay('quiz-session');
        return () => unregisterOverlay('quiz-session');
    }, [registerOverlay, unregisterOverlay]);

    // Auto-pause after 60s of inactivity
    const inactivityTimerRef = useRef(null);
    useEffect(() => {
        if (isFinished || isPaused || isLoading) {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            return;
        }
        const INACTIVITY_DELAY = 60_000;
        const resetTimer = () => {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = setTimeout(() => setIsPaused(true), INACTIVITY_DELAY);
        };
        const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
        resetTimer();
        return () => {
            events.forEach(e => window.removeEventListener(e, resetTimer));
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        };
    }, [isFinished, isPaused, isLoading, setIsPaused]);

    const [showGrid, setShowGrid] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showRestartModal, setShowRestartModal] = useState(false);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isReviewMode, setIsReviewMode] = useState(false);

    const passingScore = 75;

    const calculatedScore = useMemo(() => {
        if (!questions.length || Object.keys(answers).length === 0) return 0;
        const correctCount = questions.reduce((acc, q) => {
            const secret = onDemandAnswers[q.id];
            const userAnsId = answers[q.id];
            return acc + (userAnsId !== undefined && secret && String(userAnsId) === String(secret.correctReponseId) ? 1 : 0);
        }, 0);
        return Math.round((correctCount / questions.length) * 100);
    }, [questions, answers, onDemandAnswers]);

    const currentQuestion = questions[currentQuestionIndex];

    const hasActivatedReview = useRef(false);

    useEffect(() => {
        if (isFinished && questions.length > 0 && !hasActivatedReview.current) {
            setIsReviewMode(true);
            hasActivatedReview.current = true;
        }
        if (!isFinished) {
            setIsReviewMode(false);
            hasActivatedReview.current = false;
        }
    }, [isFinished, questions.length]);

    // Auto-trigger help tour on first quiz session
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('quiz-session') && questions.length > 0 && !isLoading && !isFinished) {
            const timer = setTimeout(() => startTour('quiz-session'), 1000);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, questions.length, isLoading, isFinished, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage]);

    useEffect(() => {
        if (!currentQuestion) return;
        setSelectedOption(answers[currentQuestion.id] !== undefined ? answers[currentQuestion.id] : null);
        setShowFeedback((mode === 'flash' && answers[currentQuestion.id] !== undefined) || isReviewMode);
    }, [currentQuestionIndex, answers, mode, currentQuestion, isReviewMode]);

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-gray-50 dark:bg-black flex items-center justify-center text-gray-900 dark:text-white transition-colors duration-500">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 dark:border-white"></div>
            </div>
        );
    }

    if (!questions.length) {
        return (
            <div className="fixed inset-0 bg-gray-50 dark:bg-black flex flex-col items-center justify-center text-gray-900 dark:text-white p-6 text-center transition-colors duration-500">
                <h2 className="text-2xl font-bold mb-4">{language === 'fr' ? 'Aucune question disponible' : 'No questions available'}</h2>
                <button
                    onClick={() => stopQuiz()}
                    className="px-6 py-2 bg-gray-200 dark:bg-white/20 rounded-xl hover:bg-gray-300 dark:hover:bg-white/30 transition font-medium"
                >
                    {language === 'fr' ? 'Fermer' : 'Close'}
                </button>
            </div>
        );
    }

    const handleOptionSelect = async (optionId) => {
        if (isPaused || isFinished || (mode === 'flash' && showFeedback)) return;
        setSelectedOption(optionId);
        submitAnswer(currentQuestion.id, optionId);
        if (mode === 'flash') {
            await loadAnswers();
            setShowFeedback(true);
        }
    };

    // Helper: true if question has a real answer (not 0/undefined/"0")
    const isRealAnswer = (qId) => {
        const v = answers[qId];
        return v !== undefined && v !== 0 && v !== '0';
    };

    const handlePrev = () => {
        // Flash mode: only allow going back to already-answered questions
        if (mode === 'flash' && !isReviewMode && currentQuestionIndex > 0) {
            const prevQuestion = questions[currentQuestionIndex - 1];
            if (prevQuestion && isRealAnswer(prevQuestion.id)) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            }
            return;
        }
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleNext = () => {
        // Flash mode: block advancing if current question not truly answered
        if (mode === 'flash' && !isReviewMode && !isRealAnswer(questions[currentQuestionIndex]?.id)) {
            return;
        }
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            if (mode === 'flash') setShowFeedback(false);
        } else {
            if (mode === 'classic' && !isReviewMode) {
                const firstUnansweredIndex = questions.findIndex(q => answers[q.id] === undefined);
                if (firstUnansweredIndex !== -1) {
                    setCurrentQuestionIndex(firstUnansweredIndex);
                } else {
                    setShowValidateModal(true);
                }
            }
        }
    };

    const handleRestart = (newMode) => {
        restartQuiz(newMode);
        setShowRestartModal(false);
        setShowSettings(false);
        setIsReviewMode(false);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progress = (Object.keys(answers).length / questions.length) * 100;

    // Results screen
    if (isFinished && !isReviewMode) {
        return (
            <ExamResults
                passingScore={passingScore}
                setIsReviewMode={setIsReviewMode}
                showRestartModal={showRestartModal}
                setShowRestartModal={setShowRestartModal}
                onRestart={handleRestart}
                questions={questions}
                answers={answers}
                onDemandAnswers={onDemandAnswers}
                stopExam={stopQuiz}
                setCurrentQuestionIndex={setCurrentQuestionIndex}
                language={language}
                isQuiz={true}
            />
        );
    }

    return (
        <div className="fixed inset-0 bg-gray-50 dark:bg-black text-gray-900 dark:text-white flex flex-col z-[100] transition-colors duration-500">
            <ExamHeader
                isReviewMode={isReviewMode}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                setShowValidateModal={setShowValidateModal}
                showGrid={showGrid}
                setShowGrid={setShowGrid}
                setShowRestartModal={setShowRestartModal}
                stopExam={stopQuiz}
                isFinished={isFinished}
                answers={answers}
                validateLabel={language === 'fr' ? 'Valider le quiz' : 'Validate Quiz'}
                title={courseTitle}
                miniNav={
                    <ExamMiniNav
                        inline
                        isReviewMode={isReviewMode}
                        questions={questions}
                        answers={answers}
                        currentQuestionIndex={currentQuestionIndex}
                        setCurrentQuestionIndex={setCurrentQuestionIndex}
                        onDemandAnswers={onDemandAnswers}
                    />
                }
            />

            {/* Mobile-only mini nav (desktop slot is inside ExamHeader above) */}
            <div className="md:hidden">
                <ExamMiniNav
                    isReviewMode={isReviewMode}
                    questions={questions}
                    answers={answers}
                    currentQuestionIndex={currentQuestionIndex}
                    setCurrentQuestionIndex={setCurrentQuestionIndex}
                    onDemandAnswers={onDemandAnswers}
                />
            </div>

            {/* Validate Confirmation Modal */}
            {showValidateModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-white/10 animate-in zoom-in-95 duration-200">
                        <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                            {language === 'fr' ? 'Valider le quiz ?' : 'Validate Quiz?'}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {language === 'fr'
                                ? `Vous avez répondu à ${Object.keys(answers).length} question(s) sur ${questions.length}. Voulez-vous vraiment valider le quiz ?`
                                : `You have answered ${Object.keys(answers).length} question(s) out of ${questions.length}. Do you really want to validate the quiz?`
                            }
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowValidateModal(false)}
                                className="flex-1 px-6 py-3 bg-gray-200 dark:bg-white/10 rounded-xl font-bold hover:bg-gray-300 dark:hover:bg-white/20 transition text-gray-900 dark:text-white"
                            >
                                {language === 'fr' ? 'Annuler' : 'Cancel'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowValidateModal(false);
                                    finishQuiz();
                                }}
                                className="flex-1 px-6 py-3 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-600 transition shadow-lg shadow-emerald-500/20"
                            >
                                {language === 'fr' ? 'Valider' : 'Validate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ExamQuestion
                isReviewMode={isReviewMode}
                isPaused={isPaused}
                setIsPaused={setIsPaused}
                calculatedScore={calculatedScore}
                passingScore={passingScore}
                timeInSeconds={timeInSeconds}
                selectedOption={selectedOption}
                showFeedback={showFeedback}
                onOptionSelect={handleOptionSelect}
                onNext={handleNext}
                onPrev={handlePrev}
                formatTime={formatTime}
                currentQuestion={currentQuestion}
                currentQuestionIndex={currentQuestionIndex}
                questions={questions}
                answers={answers}
                onDemandAnswers={onDemandAnswers}
                language={language}
                mode={mode}
                finishExam={finishQuiz}
                isQuiz={true}
            />

            {/* Footer */}
            {!isReviewMode && (
                <footer data-help="session-footer" className="p-4 h-24 flex flex-col justify-end">
                    <div className="max-w-3xl mx-auto w-full flex items-center gap-6">
                        <button
                            onClick={() => setIsPaused(!isPaused)}
                            className="p-3 bg-white dark:bg-white/10 rounded-2xl hover:bg-gray-100 dark:hover:bg-white/20 transition border border-gray-200 dark:border-transparent text-gray-700 dark:text-white"
                        >
                            {isPaused ? (
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>

                        <div className="flex-1 space-y-1.5">
                            <div className="flex justify-between text-xs font-bold text-gray-500 dark:text-gray-400 px-1">
                                <span>{formatTime(timeInSeconds)} min</span>
                                <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-blue-500 transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </footer>
            )}

            <ExamGridOverlay
                showGrid={showGrid}
                setShowGrid={setShowGrid}
                isReviewMode={isReviewMode}
                questions={questions}
                answers={answers}
                currentQuestionIndex={currentQuestionIndex}
                setCurrentQuestionIndex={setCurrentQuestionIndex}
                mode={mode}
                onDemandAnswers={onDemandAnswers}
            />

            {/* Restart Modal */}
            {showRestartModal && !isFinished && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md w-full border border-gray-200 dark:border-white/10 shadow-2xl animate-in zoom-in-95 duration-200">
                        <h3 className="text-2xl font-bold mb-4 text-center text-gray-900 dark:text-white">
                            {language === 'fr' ? 'Redémarrer le quiz ?' : 'Restart Quiz?'}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 text-center mb-8">
                            {language === 'fr' ? 'Toute votre progression actuelle sera perdue.' : 'All your current progress will be lost.'}
                        </p>
                        <div className="space-y-4">
                            <button onClick={() => handleRestart('flash')} className="w-full p-4 bg-emerald-500 text-white rounded-2xl font-bold hover:bg-emerald-600 transition flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                {language === 'fr' ? 'Mode question-réponse' : 'Flashcard Mode'}
                            </button>
                            <button onClick={() => handleRestart('classic')} className="w-full p-4 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 transition flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {language === 'fr' ? 'Mode examen' : 'Exam Mode'}
                            </button>
                            <button onClick={() => setShowRestartModal(false)} className="w-full p-3 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition mt-2">
                                {language === 'fr' ? 'Annuler' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizSession;
